import { Autocomplete, Box, Button, TextField } from "@mui/material";
import { entityGender, entityVerificationStatus } from "../../../constants/prefilled.constants";
import AdvancedSearch from "../AdvancedSearch";
import { AdvancedFilterIcon } from "../../../assets/icons/MUI/NetworkIcons";
import { colors } from "../../../theme/palette";
import AutocompleteIcon from "../../../assets/icons/MUI/ComponentsIcons";
import { useTranslation } from "react-i18next";
import { useCountryListing } from "../../../api/queries/network/useCountryListing";
import { entityGenderEnum, entityRoleEnum, entityVerificationStatusEnum } from "../../../enums/entity.enum";
import { AdvancedFilters } from "../../../types";

interface NetworkFiltersProps {
  isNetworkDataLoading: boolean;
  setLocation: (value: React.SetStateAction<string>) => void;
  nationality: string;
  setNationality: React.Dispatch<React.SetStateAction<string>>;
  gender: "" | entityGenderEnum;
  setGender: React.Dispatch<React.SetStateAction<"" | entityGenderEnum>>;
  verificationStatus: "" | entityVerificationStatusEnum;
  setVerificationStatus: React.Dispatch<React.SetStateAction<"" | entityVerificationStatusEnum>>;
  hasActiveFilters: boolean;
  roleTab: number;
  setShowModal: React.Dispatch<React.SetStateAction<boolean>>;
  setAdvancedFilters: React.Dispatch<React.SetStateAction<AdvancedFilters>>;
  networkFilters: AdvancedFilters;
  advancedFilters: AdvancedFilters;
  showModal: boolean;
  roleEntitySelected: entityRoleEnum;
  setNetworkFilters: React.Dispatch<React.SetStateAction<AdvancedFilters>>;
  clearFilters: () => void;
  location: string;
}

const NetworkFilters = ({
  isNetworkDataLoading,
  setLocation,
  nationality,
  setNationality,
  gender,
  setGender,
  verificationStatus,
  setVerificationStatus,
  hasActiveFilters,
  roleTab,
  setShowModal,
  setAdvancedFilters,
  showModal,
  roleEntitySelected,
  networkFilters,
  setNetworkFilters,
  advancedFilters,
  clearFilters,
  location,
}: NetworkFiltersProps) => {
  const { t, i18n } = useTranslation();
  const currentLanguage = i18n.language;

  const { countries, nationalities, isCountriesDataLoading } = useCountryListing();

  return (
    <Box
      sx={{
        width: "100%",
        overflowX: "auto",
        whiteSpace: "nowrap",
        padding: "0 8px 8px",
      }}
    >
      <Box
        sx={{
          height: "40px",
          width: "fit-content",
          display: "flex",
          gap: "10px",
        }}
      >
        <Autocomplete
          disablePortal
          disabled={isNetworkDataLoading || isCountriesDataLoading}
          id="countries"
          options={countries || []} // Ensure options is always an array
          value={
            // @ts-ignore
            countries.find((country) => country.value === location) || null
          }
          onChange={(event, option: any) => {
            setLocation(option?.value || "");
          }}
          sx={{
            width: "150px",
          }}
          popupIcon={<AutocompleteIcon />}
          renderInput={(params) => (
            <TextField
              {...params}
              inputProps={{
                ...params.inputProps,
                style: { fontSize: "14px" },
                endAdornment: currentLanguage === "ar" ? <AutocompleteIcon /> : <AutocompleteIcon />,
              }}
              placeholder={t("components.networkController.country")}
            />
          )}
        />
        <Autocomplete
          disablePortal
          disabled={isNetworkDataLoading}
          id="nationalities"
          options={nationalities || []}
          value={
            // @ts-ignore
            nationalities.find((nat) => nat.value === nationality) || null
          }
          onChange={(event, option: any) => {
            setNationality(option?.value || "");
          }}
          sx={{
            width: "220px",
          }}
          popupIcon={<AutocompleteIcon />}
          renderInput={(params) => (
            <TextField
              {...params}
              inputProps={{
                ...params.inputProps,
                style: { fontSize: "14px" },
              }}
              placeholder={t("components.networkController.nationality")}
            />
          )}
        />

        <Autocomplete
          disablePortal
          disabled={isNetworkDataLoading}
          id="gender"
          options={
            entityGender.map((el) => ({
              ...el,
              label: t(`constant.gender.${el.label}`),
            })) || []
          }
          value={entityGender.find((g) => g.value === gender) || null}
          onChange={(event, option: any) => {
            setGender(option?.value || "");
          }}
          sx={{
            width: "120px",
          }}
          popupIcon={<AutocompleteIcon />}
          renderInput={(params) => (
            <TextField
              {...params}
              inputProps={{
                ...params.inputProps,
                style: { fontSize: "14px" },
              }}
              placeholder={t("components.networkController.gender")}
            />
          )}
        />
        <Autocomplete
          disablePortal
          disabled={isNetworkDataLoading}
          id="verificationType"
          value={entityVerificationStatus.find((status) => status.value === verificationStatus) || null}
          options={
            entityVerificationStatus.map((el) => ({
              ...el,
              label: t(`constant.verificationStatus.${el.label}`),
            })) || []
          }
          onChange={(event, option: any) => {
            setVerificationStatus(option?.value || "");
          }}
          sx={{
            width: "150px",
          }}
          popupIcon={<AutocompleteIcon />}
          renderInput={(params) => (
            <TextField
              {...params}
              inputProps={{
                ...params.inputProps,
                style: { fontSize: "14px" },
              }}
              placeholder={t("components.networkController.verified")}
            />
          )}
        />
        {hasActiveFilters && (
          <Button disabled={isNetworkDataLoading} variant="text" color="primary" onClick={clearFilters}>
            {t("components.networkController.clear")}
          </Button>
        )}
        {roleTab !== 0 && (
          <Button
            disabled={isNetworkDataLoading}
            variant="outlined-md"
            onClick={() => {
              setShowModal(true);
              setAdvancedFilters(networkFilters);
            }}
            startIcon={<AdvancedFilterIcon />}
            sx={{
              color: colors.Yellow200,
              border: `1px solid ${colors.Yellow200}`,
              padding: "7.5px 4px 7.5px 5px",
              "&:hover": {
                color: colors.Yellow200,
                border: `1px solid ${colors.Yellow200}`,
                backgroundColor: colors.Yellow25,
              },
            }}
          >
            {t("components.networkController.advancedFilter")}
          </Button>
        )}
        <AdvancedSearch
          openAdvancedSearch={showModal}
          closeAdvancedSearch={() => setShowModal(false)}
          entityRole={roleEntitySelected}
          networkFilters={networkFilters}
          setNetworkFilters={setNetworkFilters}
          advancedFilters={advancedFilters}
          setAdvancedFilters={setAdvancedFilters}
        />
      </Box>
    </Box>
  );
};

export default NetworkFilters;
